<template>
  <!-- ===== 데스크톱: V1 레이아웃 ===== -->
  <div v-if="!isMobile" class="home" ref="container">
    <div class="sections-wrapper" :style="wrapperStyle">
      <V1Hero
        ref="section0"
        :class="{ 'is-active': isSectionActive(0) }"
      />
      <V1Premium
        ref="premiumSection"
        :items="premiumItems"
        :is-active="isSectionActive(1)"
        :class="{ 'is-active': isSectionActive(1) }"
        @next-section="goToNextSection"
        @prev-section="goToPrevSection"
      />
      <V1Community
        ref="communitySection"
        :is-active="isSectionActive(2)"
        :class="{ 'is-active': isSectionActive(2) }"
        @next-section="goToNextSection"
        @prev-section="goToPrevSection"
      />
      <V1Specialization
        ref="specSection"
        :is-active="isSectionActive(3)"
        :class="{ 'is-active': isSectionActive(3) }"
        @next-section="goToNextSection"
        @prev-section="goToPrevSection"
      />
      <V1Unit
        ref="section4"
        :is-active="isSectionActive(4)"
        :class="{ 'is-active': isSectionActive(4) }"
      />
      <V1Direction
        ref="section5"
        :is-active="isSectionActive(5)"
        :class="{ 'is-active': isSectionActive(5) }"
      />
    </div>

    <V1Footer :class="'footer-fixed'" />

    <SectionProgress
      :current-section="currentSection"
      @go-to="scrollToSection"
    />
  </div>

  <!-- ===== 모바일: Version2 레이아웃 ===== -->
  <MobileVersion2 v-else />
</template>

<script>
// 데스크톱 V1 컴포넌트
import V1Hero from '@/components/web/home-v1/HeroSection.vue'
import V1Premium from '@/components/web/home-v1/PremiumSection.vue'
import V1Community from '@/components/web/home-v1/CommunitySection.vue'
import V1Specialization from '@/components/web/home-v1/SpecializationSection.vue'
import V1Unit from '@/components/web/home-v1/UnitSection.vue'
import V1Direction from '@/components/web/home-v1/DirectionSection.vue'
import V1Footer from '@/components/web/home-v1/FooterSection.vue'
import SectionProgress from '@/components/web/common/SectionProgress.vue'

// 모바일 Version2
import MobileVersion2 from '@/views/MobileVersion2View.vue'

export default {
  name: 'HomeView',
  components: {
    V1Hero, V1Premium, V1Community, V1Specialization, V1Unit, V1Direction, V1Footer,
    SectionProgress,
    MobileVersion2
  },
  data() {
    return {
      isMobile: false,

      // ===== 데스크톱 V1 상태 =====
      currentSection: 0,
      totalSections: 6,
      showFooter: false,
      isScrolling: false,
      touchStartY: 0,
      premiumItems: [
        {
          eng: 'Location',
          title: '직주근접 신중심지',
          desc: '수원 당수지구 중심에 위치하여\n삼성전자 수원사업장, 호매실 신도시 등\n수원 서남부 핵심 생활권을 누리는\n직주 근접 프리미엄 입지',
          image: 'location'
        },
        {
          eng: 'Transportation',
          title: '사통팔달 교통요지',
          desc: '수인분당선 호매실역(예정), 신분당선 연장(예정),\n봉담과선교, 수원문산고속도로 등\n사통팔달 광역교통망의 중심 교통요지',
          image: 'transportation'
        },
        {
          eng: 'Infrastructure',
          title: '원스톱 생활인프라',
          desc: '당수초, 호매실중 등 도보 통학권 학세권 형성 및\n롯데몰 수원, 홈플러스, 호매실 상업지구 등\n풍부한 생활 인프라 인접',
          image: 'infrastructure'
        },
        {
          eng: 'Nature',
          title: '청정자연 조성',
          desc: '단지 인근 당수천, 당수공원,\n호매실 중앙공원을 품은 쾌적한 자연환경과\n대단지 공원형 조경으로 힐링 라이프 조성',
          image: 'nature'
        }
      ],

    }
  },
  computed: {
    // 데스크톱
    wrapperStyle() {
      const base = this.currentSection * 100
      const footerOffset = this.showFooter ? 280 : 0
      return {
        transform: `translateY(-${base}vh) translateY(-${footerOffset}px)`
      }
    },
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)

    this.$nextTick(() => {
      if (!this.isMobile) {
        this.setupDesktopListeners()
      }
    })
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
    this.teardownDesktopListeners()
  },
  watch: {
    isMobile(newVal, oldVal) {
      if (newVal === oldVal) return
      if (newVal) {
        this.teardownDesktopListeners()
        this.currentSection = 0
        this.showFooter = false
      } else {
        this.$nextTick(() => { this.setupDesktopListeners() })
      }
    }
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },

    // ===== 데스크톱 V1 메서드 =====
    isSectionActive(index) {
      return this.currentSection === index
    },
    setupDesktopListeners() {
      const el = this.$refs.container
      if (!el) return
      el.addEventListener('wheel', this.handleWheel, { passive: false })
      el.addEventListener('touchstart', this.handleTouchStart, { passive: true })
      el.addEventListener('touchend', this.handleTouchEnd, { passive: true })
    },
    teardownDesktopListeners() {
      const el = this.$refs.container
      if (!el) return
      el.removeEventListener('wheel', this.handleWheel)
      el.removeEventListener('touchstart', this.handleTouchStart)
      el.removeEventListener('touchend', this.handleTouchEnd)
    },
    handleWheel(e) {
      e.preventDefault()
      if (this.isScrolling) return

      const direction = e.deltaY > 0 ? 'down' : 'up'

      if (this.currentSection === 1) {
        const s = this.$refs.premiumSection
        if (s) { s.handleScroll(direction) }
        return
      }
      if (this.currentSection === 2) {
        const s = this.$refs.communitySection
        if (s) { s.handleScroll(direction) }
        return
      }
      if (this.currentSection === 3) {
        const s = this.$refs.specSection
        if (s) { s.handleScroll(direction) }
        return
      }

      if (e.deltaY > 0 && this.currentSection === this.totalSections - 1 && !this.showFooter) {
        this.showFooter = true
        this.isScrolling = true
        setTimeout(() => { this.isScrolling = false }, 800)
        return
      }
      if (e.deltaY < 0 && this.showFooter) {
        this.showFooter = false
        this.isScrolling = true
        setTimeout(() => { this.isScrolling = false }, 800)
        return
      }

      if (e.deltaY > 0 && this.currentSection < this.totalSections - 1) {
        this.scrollToSection(this.currentSection + 1)
      } else if (e.deltaY < 0 && this.currentSection > 0) {
        this.scrollToSection(this.currentSection - 1)
      }
    },
    handleTouchStart(e) {
      this.touchStartY = e.touches[0].clientY
    },
    handleTouchEnd(e) {
      if (this.isScrolling) return
      const diff = this.touchStartY - e.changedTouches[0].clientY
      const direction = diff > 0 ? 'down' : 'up'

      if (this.currentSection === 1) {
        const s = this.$refs.premiumSection
        if (s && Math.abs(diff) > 50) { s.handleScroll(direction) }
        return
      }
      if (this.currentSection === 2) {
        const s = this.$refs.communitySection
        if (s && Math.abs(diff) > 50) { s.handleScroll(direction) }
        return
      }
      if (this.currentSection === 3) {
        const s = this.$refs.specSection
        if (s && Math.abs(diff) > 50) { s.handleScroll(direction) }
        return
      }

      if (diff > 50 && this.currentSection < this.totalSections - 1) {
        this.scrollToSection(this.currentSection + 1)
      } else if (diff < -50 && this.currentSection > 0) {
        this.scrollToSection(this.currentSection - 1)
      }
    },
    scrollToSection(index) {
      this.isScrolling = true
      this.currentSection = index
      window.dispatchEvent(new CustomEvent('section-change', { detail: index }))
      setTimeout(() => { this.isScrolling = false }, 1800)
    },
    goToNextSection() {
      if (this.currentSection < this.totalSections - 1) {
        this.scrollToSection(this.currentSection + 1)
      }
    },
    goToPrevSection() {
      if (this.currentSection > 0) {
        this.scrollToSection(this.currentSection - 1)
      }
    },

  }
}
</script>

<style scoped>
/* ===== 데스크톱 V1 ===== */
.home {
  height: 100vh;
  overflow: hidden;
  position: relative;
}

.footer-fixed {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 0;
}

.sections-wrapper {
  position: relative;
  z-index: 1;
  transition: transform 1.6s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}

</style>
